import { motion } from "framer-motion";
import { TrendingUp, IndianRupee } from "lucide-react";

/**
 * CSS/SVG-based animated 3D hero visual — replaces R3F Canvas.
 * Avoids the known R3F + React 19 dev-mode reconciler issue and is faster on mobile.
 */
export default function Hero3DVisual() {
  return (
    <div
      data-testid="hero-3d-visual"
      className="absolute inset-0 [perspective:1400px]"
    >
      {/* Ambient gradient bloom */}
      <div
        aria-hidden
        className="absolute inset-0 rounded-[40px]"
        style={{
          background:
            "radial-gradient(circle at 60% 35%, rgba(16,185,129,0.22), transparent 55%), radial-gradient(circle at 25% 70%, rgba(11,27,61,0.10), transparent 55%)",
        }}
      />

      {/* Soft grid floor */}
      <div
        aria-hidden
        className="absolute left-1/2 bottom-10 -translate-x-1/2 w-[120%] h-[55%] [transform:rotateX(72deg)] origin-bottom opacity-30"
        style={{
          background:
            "linear-gradient(to right, rgba(11,27,61,0.18) 1px, transparent 1px) 0 0/52px 100%, linear-gradient(to bottom, rgba(11,27,61,0.18) 1px, transparent 1px) 0 0/100% 52px",
          maskImage:
            "radial-gradient(circle at 50% 30%, black 20%, transparent 70%)",
          WebkitMaskImage:
            "radial-gradient(circle at 50% 30%, black 20%, transparent 70%)",
        }}
      />

      {/* Coin 1 — gold */}
      <motion.div
        animate={{ y: [0, -22, 0], rotateY: [0, 360, 720], rotateX: [12, 22, 12] }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        className="absolute left-[18%] top-[38%] w-24 h-24 [transform-style:preserve-3d]"
      >
        <Coin label="₹" gradient="linear-gradient(135deg,#F4D580,#D4AF37 45%,#9c7e1f)" ring="#caa029" />
      </motion.div>

      {/* Coin 2 — emerald */}
      <motion.div
        animate={{ y: [0, -18, 0], rotateY: [0, -360, -720], rotateX: [-8, 8, -8] }}
        transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
        className="absolute right-[12%] top-[14%] w-28 h-28 [transform-style:preserve-3d]"
      >
        <Coin label="₹" gradient="linear-gradient(135deg,#34D399,#10B981 45%,#0a8f63)" ring="#0a8f63" />
      </motion.div>

      {/* Coin 3 — navy */}
      <motion.div
        animate={{ y: [0, 16, 0], rotateY: [0, 360, 720] }}
        transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
        className="absolute left-[55%] top-[60%] w-20 h-20 [transform-style:preserve-3d]"
      >
        <Coin label="₹" gradient="linear-gradient(135deg,#1A2F5B,#0B1B3D 45%,#070f24)" ring="#1A2F5B" />
      </motion.div>

      {/* Glass sphere */}
      <motion.div
        animate={{ y: [0, -14, 0], scale: [1, 1.06, 1] }}
        transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
        className="absolute right-[28%] top-[58%] w-32 h-32 rounded-full"
        style={{
          background:
            "radial-gradient(circle at 30% 25%, rgba(255,255,255,0.85), rgba(16,185,129,0.55) 35%, rgba(11,27,61,0.6) 80%)",
          boxShadow:
            "inset -8px -10px 30px rgba(0,0,0,0.25), 0 25px 50px -12px rgba(11,27,61,0.4)",
        }}
      />

      {/* Bar chart */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1.2, delay: 0.6 }}
        className="absolute left-[8%] bottom-[18%] flex items-end gap-2.5 [transform:rotateX(8deg)_rotateY(-8deg)] [transform-style:preserve-3d]"
      >
        {[
          { h: 36, c: "linear-gradient(to top,#10B981,#34D399)" },
          { h: 56, c: "linear-gradient(to top,#00A65A,#10B981)" },
          { h: 84, c: "linear-gradient(to top,#0B1B3D,#1A2F5B)" },
          { h: 112, c: "linear-gradient(to top,#D4AF37,#F4D580)" },
        ].map((b, i) => (
          <motion.span
            key={i}
            initial={{ height: 0 }}
            animate={{ height: b.h }}
            transition={{ duration: 1.2, delay: 0.4 + i * 0.15, ease: [0.22, 1, 0.36, 1] }}
            className="w-6 rounded-t-md shadow-lg"
            style={{ background: b.c }}
          />
        ))}
      </motion.div>

      {/* Trend line SVG */}
      <svg
        aria-hidden
        className="absolute left-[20%] right-[20%] top-[20%] w-[60%] h-[55%] pointer-events-none"
        viewBox="0 0 400 240"
        fill="none"
      >
        <defs>
          <linearGradient id="trendG" x1="0" x2="1">
            <stop offset="0%" stopColor="#10B981" stopOpacity="0" />
            <stop offset="40%" stopColor="#10B981" stopOpacity="0.9" />
            <stop offset="100%" stopColor="#D4AF37" stopOpacity="1" />
          </linearGradient>
        </defs>
        <motion.path
          d="M 10 200 Q 80 160 120 150 T 220 90 T 380 30"
          stroke="url(#trendG)"
          strokeWidth="2.5"
          strokeLinecap="round"
          initial={{ pathLength: 0, opacity: 0 }}
          animate={{ pathLength: 1, opacity: 1 }}
          transition={{ duration: 2.4, delay: 0.8, ease: "easeInOut" }}
        />
        <motion.circle
          cx="380"
          cy="30"
          r="6"
          fill="#D4AF37"
          initial={{ scale: 0 }}
          animate={{ scale: [0, 1.4, 1] }}
          transition={{ duration: 0.8, delay: 3.2 }}
        />
      </svg>

      {/* Floating mini badge with trend icon */}
      <motion.div
        animate={{ y: [0, -8, 0] }}
        transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
        className="absolute left-[34%] top-[18%] bg-white border border-black/5 rounded-2xl px-3 py-2 shadow-xl flex items-center gap-2"
      >
        <span className="w-7 h-7 rounded-lg bg-[#10B981]/15 flex items-center justify-center">
          <TrendingUp className="w-4 h-4 text-[#00A65A]" />
        </span>
        <div className="leading-tight">
          <div className="overline text-[8px] text-[#0B1B3D]/50">12 Yr CAGR</div>
          <div className="font-display text-base text-[#0B1B3D]">14.3%</div>
        </div>
      </motion.div>

      {/* Tiny rupee chip */}
      <motion.div
        animate={{ y: [0, 10, 0], rotate: [-4, 4, -4] }}
        transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
        className="absolute right-[42%] bottom-[10%] w-12 h-12 rounded-2xl bg-[#0B1B3D] text-white flex items-center justify-center shadow-2xl"
      >
        <IndianRupee className="w-5 h-5 text-[#D4AF37]" />
      </motion.div>
    </div>
  );
}

function Coin({ label, gradient, ring }) {
  return (
    <div className="relative w-full h-full">
      {/* Coin face */}
      <div
        className="absolute inset-0 rounded-full flex items-center justify-center font-display text-3xl font-medium text-white"
        style={{
          background: gradient,
          boxShadow: `inset -4px -6px 14px rgba(0,0,0,0.32), inset 4px 6px 14px rgba(255,255,255,0.28), 0 25px 40px -12px rgba(11,27,61,0.45)`,
          border: `1px solid ${ring}`,
        }}
      >
        <span style={{ textShadow: "0 1px 0 rgba(0,0,0,0.25)" }}>{label}</span>
      </div>
      {/* Edge highlight */}
      <div
        className="absolute inset-0 rounded-full pointer-events-none"
        style={{
          background:
            "conic-gradient(from 90deg, rgba(255,255,255,0.0), rgba(255,255,255,0.45), rgba(255,255,255,0.0) 25%, rgba(255,255,255,0.0) 75%, rgba(255,255,255,0.35), rgba(255,255,255,0.0))",
          mixBlendMode: "screen",
          opacity: 0.6,
        }}
      />
    </div>
  );
}
